import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, PackageIcon, TagIcon, ShoppingBagIcon, UsersIcon, BarChart3Icon, TagsIcon, SettingsIcon, LogOutIcon } from 'lucide-react';
interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}
const SidebarLink: React.FC<SidebarLinkProps> = ({
  to,
  icon,
  label,
  isActive
}) => <Link to={to} className={`flex items-center px-4 py-3 rounded-md transition-colors ${isActive ? 'bg-yellow-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-607">
    <span className="mr-3" data-id="element-608">{icon}</span>
    <span data-id="element-609">{label}</span>
  </Link>;
export const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const [salesData] = useState([{
    month: 'Jan',
    revenue: 12500
  }, {
    month: 'Feb',
    revenue: 14000
  }, {
    month: 'Mar',
    revenue: 15800
  }, {
    month: 'Apr',
    revenue: 16200
  }, {
    month: 'May',
    revenue: 18000
  }, {
    month: 'Jun',
    revenue: 19500
  }]);
  return <div className="min-h-screen bg-gray-100 dark:bg-gray-900 w-full" data-id="element-610">
      <div className="flex" data-id="element-611">
        {/* Sidebar */}
        <aside className="w-64 bg-white dark:bg-gray-800 min-h-screen p-4 hidden md:block" data-id="element-612">
          <div className="mb-8" data-id="element-613">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-614">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-615">
              Tech & Gold Market
            </p>
          </div>
          <nav className="space-y-1" data-id="element-616">
            <SidebarLink to="/admin" icon={<LayoutDashboardIcon size={20} data-id="element-618" />} label="Dashboard" isActive={location.pathname === '/admin'} data-id="element-617" />
            <SidebarLink to="/admin/products" icon={<PackageIcon size={20} data-id="element-620" />} label="Products" isActive={location.pathname === '/admin/products'} data-id="element-619" />
            <SidebarLink to="/admin/categories" icon={<TagIcon size={20} data-id="element-622" />} label="Categories" isActive={location.pathname === '/admin/categories'} data-id="element-621" />
            <SidebarLink to="/admin/orders" icon={<ShoppingBagIcon size={20} data-id="element-624" />} label="Orders" isActive={location.pathname === '/admin/orders'} data-id="element-623" />
            <SidebarLink to="/admin/customers" icon={<UsersIcon size={20} data-id="element-626" />} label="Customers" isActive={location.pathname === '/admin/customers'} data-id="element-625" />
            <SidebarLink to="/admin/statistics" icon={<BarChart3Icon size={20} data-id="element-628" />} label="Statistics" isActive={location.pathname === '/admin/statistics'} data-id="element-627" />
            <SidebarLink to="/admin/promotions" icon={<TagsIcon size={20} data-id="element-630" />} label="Promotions" isActive={location.pathname === '/admin/promotions'} data-id="element-629" />
          </nav>
          <div className="mt-auto pt-8 border-t border-gray-200 dark:border-gray-700 space-y-1" data-id="element-631">
            <SidebarLink to="/admin/settings" icon={<SettingsIcon size={20} data-id="element-633" />} label="Settings" isActive={location.pathname === '/admin/settings'} data-id="element-632" />
            <Link to="/" className="flex items-center px-4 py-3 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-634">
              <LogOutIcon size={20} className="mr-3" data-id="element-635" />
              <span data-id="element-636">Logout</span>
            </Link>
          </div>
        </aside>
        {/* Main Content */}
        <main className="flex-1 p-6" data-id="element-637">
          <div className="mb-8" data-id="element-638">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-639">
              Dashboard Overview
            </h1>
            <p className="text-gray-600 dark:text-gray-400" data-id="element-640">
              Welcome back, Admin! Here's what's happening with your store
              today.
            </p>
          </div>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-641">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" data-id="element-642">
              <div className="flex items-center justify-between mb-4" data-id="element-643">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-644">
                  Total Revenue
                </h3>
                <span className="p-2 bg-green-100 dark:bg-green-900 rounded-md" data-id="element-645">
                  <BarChart3Icon size={18} className="text-green-600 dark:text-green-400" data-id="element-646" />
                </span>
              </div>
              <div className="flex items-baseline" data-id="element-647">
                <span className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-648">
                  $96,000
                </span>
                <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400" data-id="element-649">
                  +8.2%
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-650">
                Compared to last month
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" data-id="element-651">
              <div className="flex items-center justify-between mb-4" data-id="element-652">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-653">
                  Total Orders
                </h3>
                <span className="p-2 bg-blue-100 dark:bg-blue-900 rounded-md" data-id="element-654">
                  <ShoppingBagIcon size={18} className="text-blue-600 dark:text-blue-400" data-id="element-655" />
                </span>
              </div>
              <div className="flex items-baseline" data-id="element-656">
                <span className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-657">
                  342
                </span>
                <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400" data-id="element-658">
                  +4.5%
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-659">
                Compared to last month
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" data-id="element-660">
              <div className="flex items-center justify-between mb-4" data-id="element-661">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-662">
                  Total Products
                </h3>
                <span className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-md" data-id="element-663">
                  <PackageIcon size={18} className="text-yellow-600 dark:text-yellow-400" data-id="element-664" />
                </span>
              </div>
              <div className="flex items-baseline" data-id="element-665">
                <span className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-666">
                  128
                </span>
                <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400" data-id="element-667">
                  +12.3%
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-668">
                New products this month
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" data-id="element-669">
              <div className="flex items-center justify-between mb-4" data-id="element-670">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-671">
                  Total Customers
                </h3>
                <span className="p-2 bg-purple-100 dark:bg-purple-900 rounded-md" data-id="element-672">
                  <UsersIcon size={18} className="text-purple-600 dark:text-purple-400" data-id="element-673" />
                </span>
              </div>
              <div className="flex items-baseline" data-id="element-674">
                <span className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-675">
                  1,205
                </span>
                <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400" data-id="element-676">
                  +2.8%
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-677">
                New customers this month
              </p>
            </div>
          </div>
          {/* Sales Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8" data-id="element-678">
            <div className="flex justify-between items-center mb-6" data-id="element-679">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-680">
                Revenue Overview
              </h3>
              <select className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-681">
                <option value="last6months" data-id="element-682">Last 6 Months</option>
                <option value="last12months" data-id="element-683">Last 12 Months</option>
                <option value="thisyear" data-id="element-684">This Year</option>
              </select>
            </div>
            <div className="h-64" data-id="element-685">
              <div className="flex h-full items-end" data-id="element-686">
                {salesData.map((data, index) => <div key={index} className="flex-1 flex flex-col items-center" data-id="element-687">
                    <div className="w-full bg-yellow-500 rounded-t-md mx-1" style={{
                  height: `${data.revenue / 20000 * 100}%`
                }} data-id="element-688"></div>
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400" data-id="element-689">
                      {data.month}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Recent Orders */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden" data-id="element-690">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700" data-id="element-691">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-692">
                Recent Orders
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-693">
              <table className="w-full" data-id="element-694">
                <thead className="bg-gray-50 dark:bg-gray-900" data-id="element-695">
                  <tr data-id="element-696">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-697">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-698">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-699">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-700">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-701">
                      Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-702">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-703">
                  <tr data-id="element-704">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-705">
                      #ORD-7245
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-706">
                      John Smith
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-707">
                      Aug 06, 2023
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-708">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" data-id="element-709">
                        Completed
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-710">
                      $1,249.00
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-711">
                      <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-712">
                        View
                      </button>
                    </td>
                  </tr>
                  <tr data-id="element-713">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-714">
                      #ORD-7244
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-715">
                      Emily Johnson
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-716">
                      Aug 05, 2023
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-717">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" data-id="element-718">
                        Processing
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-719">
                      $2,084.00
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-720">
                      <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-721">
                        View
                      </button>
                    </td>
                  </tr>
                  <tr data-id="element-722">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-723">
                      #ORD-7243
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-724">
                      Michael Brown
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-725">
                      Aug 05, 2023
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-726">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" data-id="element-727">
                        Shipped
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-728">
                      $349.00
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-729">
                      <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-730">
                        View
                      </button>
                    </td>
                  </tr>
                  <tr data-id="element-731">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-732">
                      #ORD-7242
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-733">
                      Sarah Wilson
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-734">
                      Aug 04, 2023
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-735">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" data-id="element-736">
                        Cancelled
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-737">
                      $899.00
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-738">
                      <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-739">
                        View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-center" data-id="element-740">
              <Link to="/admin/orders" className="text-sm text-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium" data-id="element-741">
                View All Orders
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>;
};